import { DeleteIcon, InfoIcon } from "@rothoblaas/design-system";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type PointerEvent,
  type RefObject,
  type SetStateAction,
} from "react";
import Icon from "../../../components/ui/Icon";
import useI18n from "../../../hooks/useTranslations";
import {
  DIFFERENT_BOARD_POSITION_END,
  DIFFERENT_BOARD_POSITION_START,
  type DifferentBoardPosition,
} from "../../../http/deckDesigner/types/configurations";
import CartesianPlane, { type PlaneBounds, type PlaneCoordinate, type PlanePoint } from "./CartesianPlane";
import type { DeckBoardSegment, DifferentBoardKey } from "./DeckBoardsLayer";
import {
  getClosingCoordinate,
  getNextCoordinate,
  getTypedNextCoordinate,
  isSegmentNarrowerThanBoardWidth,
  parseTypedDistance,
} from "./deckDrawingGeometry";
import DeckDrawingLayers, { type DifferentBoardDragHandler } from "./DeckDrawingLayers";
import { getCoordinateBounds } from "./deckGeometry";
import type { SlopeArrow } from "./deckOutline";
import { createPlanePointStore } from "./planePointStore";
import styles from "./DeckDrawingPlane.module.css";

export type DeckShape = "rectangle" | "lShape";

type ShapeConfig = {
  lineClassName: string;
  shapeClassName: string;
};

type SideDragState = {
  coordinates: PlaneCoordinate[];
  pointerId: number;
  sideIndex: number;
};

type DifferentBoardDragState = {
  key: DifferentBoardKey;
  pointerId: number;
};

type DeckDrawingPlaneProps = {
  boardSegments?: DeckBoardSegment[];
  boardWidth?: number | null;
  coordinates: PlaneCoordinate[];
  // When true, hides the interactive grid/axis chrome and fits the whole deck in view (for a clean export snapshot).
  exportMode?: boolean;
  // View only (no write permission): pan and zoom, but no drawing, dragging or editing.
  readOnly?: boolean;
  finishCoordinates: (coordinates: PlaneCoordinate[]) => PlaneCoordinate[] | null;
  hasHorizontalBoards?: boolean | null;
  onClear?: () => void;
  onDifferentBoardPositionChange?: (key: DifferentBoardKey, position: DifferentBoardPosition) => void;
  onDimensionChange?: (sideCode: string, value: number) => boolean;
  onShowHint?: () => void;
  planeContentRef?: RefObject<HTMLDivElement | null>;
  pointLimit: number;
  setCoordinates: Dispatch<SetStateAction<PlaneCoordinate[]>>;
  shape: DeckShape;
  // The terrain's slope arrow, shown over the finished deck - null on flat terrain.
  slopeArrow?: SlopeArrow | null;
};

const EMPTY_BOARD_SEGMENTS: DeckBoardSegment[] = [];

const isTypingInFormField = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
};

const getDraggedSideCoordinates = (
  coordinates: PlaneCoordinate[],
  sideIndex: number,
  point: PlanePoint,
): PlaneCoordinate[] | null => {
  const startCoordinate = coordinates[sideIndex];
  const endIndex = (sideIndex + 1) % coordinates.length;
  const endCoordinate = coordinates[endIndex];

  if (!startCoordinate || !endCoordinate) {
    return null;
  }

  const isHorizontal = startCoordinate.y === endCoordinate.y;
  const isVertical = startCoordinate.x === endCoordinate.x;

  if (!isHorizontal && !isVertical) {
    return null;
  }

  return coordinates.map((coordinate, index) => {
    if (index !== sideIndex && index !== endIndex) {
      return coordinate;
    }

    return isHorizontal ? { ...coordinate, y: point.y } : { ...coordinate, x: point.x };
  });
};

const getDifferentBoardPositionForPoint = (
  segment: DeckBoardSegment,
  point: PlanePoint,
): DifferentBoardPosition | null => {
  if (!segment.differentBoardDirection || !segment.differentBoardRegion) {
    return null;
  }

  const { maxX, maxY, minX, minY } = segment.differentBoardRegion;

  if (segment.differentBoardDirection === "horizontal") {
    return point.y >= (minY + maxY) / 2 ? DIFFERENT_BOARD_POSITION_START : DIFFERENT_BOARD_POSITION_END;
  }

  return point.x <= (minX + maxX) / 2 ? DIFFERENT_BOARD_POSITION_START : DIFFERENT_BOARD_POSITION_END;
};

const DeckDrawingPlane: FC<DeckDrawingPlaneProps> = ({
  boardSegments = EMPTY_BOARD_SEGMENTS,
  boardWidth,
  coordinates,
  exportMode = false,
  readOnly = false,
  finishCoordinates,
  hasHorizontalBoards,
  onClear,
  onDifferentBoardPositionChange,
  onDimensionChange,
  onShowHint,
  planeContentRef,
  pointLimit,
  setCoordinates,
  shape,
  slopeArrow = null,
}) => {
  const i18n = useI18n();
  const commonTranslation = i18n.translation.common;
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const draftCoordinatesRef = useRef<PlaneCoordinate[] | null>(null);
  const sideDragRef = useRef<SideDragState | null>(null);
  const differentBoardDragRef = useRef<DifferentBoardDragState | null>(null);
  // Owned here (not by the plane) so the typed-distance commit below can read the hover point.
  const [pointStore] = useState(createPlanePointStore);
  const [draftCoordinates, setDraftCoordinates] = useState<PlaneCoordinate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draggedSideIndex, setDraggedSideIndex] = useState<number | null>(null);
  const [typedDistance, setTypedDistance] = useState("");
  const shapeConfig = useMemo<ShapeConfig>(
    () =>
      shape === "rectangle"
        ? {
            shapeClassName: styles.rectangle,
            lineClassName: styles.rectangleLine,
          }
        : {
            shapeClassName: styles.lShape,
            lineClassName: styles.lShapeLine,
          },
    [shape],
  );

  useEffect(() => {
    setError(null);
    draftCoordinatesRef.current = null;
    sideDragRef.current = null;
    differentBoardDragRef.current = null;
    setDraftCoordinates(null);
    setDraggedSideIndex(null);
  }, [shape]);

  const addCoordinate = (nextPoint: PlanePoint) => {
    setCoordinates((currentCoordinates) => {
      if (currentCoordinates.length >= pointLimit) {
        return currentCoordinates;
      }

      const nextCoordinate = getNextCoordinate(currentCoordinates, nextPoint, pointLimit);

      if (!nextCoordinate) {
        setError(commonTranslation.chooseSecondPointDifferentXY);
        return currentCoordinates;
      }

      const previousCoordinate = currentCoordinates[currentCoordinates.length - 1];

      if (
        previousCoordinate &&
        isSegmentNarrowerThanBoardWidth(previousCoordinate, nextCoordinate, boardWidth, hasHorizontalBoards)
      ) {
        setError(calculationTranslation.sideNarrowerThanBoardWidth);
        return currentCoordinates;
      }

      const nextCoordinates = [...currentCoordinates, nextCoordinate];
      const shouldAutoClose = nextCoordinates.length === pointLimit - 1;
      const closingCoordinate = shouldAutoClose ? getClosingCoordinate(nextCoordinates, pointLimit) : null;
      const coordinatesToFinish = closingCoordinate ? [...nextCoordinates, closingCoordinate] : nextCoordinates;
      const finishedCoordinates =
        coordinatesToFinish.length === pointLimit ? finishCoordinates(coordinatesToFinish) : null;

      if (shouldAutoClose && !closingCoordinate) {
        // Reject the whole attempt rather than leaving the invalid point placed - this is the
        // point right before auto-close, so a failure here means it can't close into a valid
        // shape (e.g. a side ending up no longer than the parallel one already drawn).
        setError(commonTranslation.chooseSecondPointDifferentXY);
        return currentCoordinates;
      }

      if (coordinatesToFinish.length === pointLimit && !finishedCoordinates) {
        setError(commonTranslation.chooseSecondPointDifferentXY);
        return currentCoordinates;
      }

      setError(null);

      return finishedCoordinates ?? nextCoordinates;
    });
  };

  // Lets the user type the next side's exact length instead of relying on the mouse position:
  // point roughly where the side should go and type its length - the point is only set on Enter,
  // so partial input (e.g. still typing "3.20") never gets committed early.
  const commitTypedDistance = (distanceText: string) => {
    const distance = parseTypedDistance(distanceText);
    const hoverPoint = pointStore.getLatest();

    if (distance != null && hoverPoint) {
      const nextCoordinate = getTypedNextCoordinate(coordinates, pointLimit, hoverPoint, distance);

      if (nextCoordinate) {
        addCoordinate({ ...nextCoordinate, pixelX: 0, pixelY: 0 });
      }
    }

    setTypedDistance("");
  };

  useEffect(() => {
    setTypedDistance("");
  }, [coordinates.length]);

  useEffect(() => {
    const isActivelyDrawing = coordinates.length > 0 && coordinates.length < pointLimit - 1;

    if (readOnly || !isActivelyDrawing) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingInFormField(event.target)) {
        return;
      }

      if (event.key >= "0" && event.key <= "9") {
        setTypedDistance((current) => current + event.key);
        return;
      }

      if (event.key === "." || event.key === ",") {
        setTypedDistance((current) => (current === "" || current.includes(".") ? current : `${current}.`));
        return;
      }

      if (event.key === "Backspace") {
        setTypedDistance((current) => current.slice(0, -1));
        return;
      }

      if (event.key === "Escape") {
        setTypedDistance("");
        return;
      }

      if (event.key === "Enter") {
        // Skip waiting for the debounce when the user explicitly confirms.
        commitTypedDistance(typedDistance);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, pointLimit, readOnly, typedDistance]);

  const clearCoordinates = () => {
    draftCoordinatesRef.current = null;
    sideDragRef.current = null;
    differentBoardDragRef.current = null;
    setDraftCoordinates(null);
    setDraggedSideIndex(null);
    setCoordinates([]);
    setError(null);
    onClear?.();
  };

  // The drag handlers below are stable while their inputs are, so the memoized DeckDrawingLayers
  // skips the renders it has nothing to redraw for.
  const startSideDrag = useCallback(
    (event: PointerEvent<SVGLineElement>, sideIndex: number) => {
      if (event.button !== 0 || coordinates.length !== pointLimit) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      sideDragRef.current = {
        coordinates,
        pointerId: event.pointerId,
        sideIndex,
      };
      draftCoordinatesRef.current = coordinates;
      setDraftCoordinates(coordinates);
      setDraggedSideIndex(sideIndex);
      setError(null);
    },
    [coordinates, pointLimit],
  );

  const dragSide = useCallback(
    (
      event: PointerEvent<SVGLineElement>,
      toPlanePointFromClient: (clientX: number, clientY: number) => PlanePoint | null,
    ) => {
      event.preventDefault();
      event.stopPropagation();

      const sideDrag = sideDragRef.current;

      if (!sideDrag || sideDrag.pointerId !== event.pointerId) {
        return;
      }

      const nextPoint = toPlanePointFromClient(event.clientX, event.clientY);

      if (!nextPoint) {
        return;
      }

      const draggedCoordinates = getDraggedSideCoordinates(sideDrag.coordinates, sideDrag.sideIndex, nextPoint);
      const finishedCoordinates = draggedCoordinates ? finishCoordinates(draggedCoordinates) : null;

      if (!finishedCoordinates) {
        setError(commonTranslation.chooseSecondPointDifferentXY);
        return;
      }

      draftCoordinatesRef.current = finishedCoordinates;
      setDraftCoordinates(finishedCoordinates);
      setError(null);
    },
    [commonTranslation.chooseSecondPointDifferentXY, finishCoordinates],
  );

  const finishSideDrag = useCallback(
    (event: PointerEvent<SVGLineElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const nextCoordinates = draftCoordinatesRef.current;

      if (nextCoordinates) {
        setCoordinates(nextCoordinates);
      }

      draftCoordinatesRef.current = null;
      sideDragRef.current = null;
      setDraftCoordinates(null);
      setDraggedSideIndex(null);
    },
    [setCoordinates],
  );

  const moveDifferentBoardToPoint = useCallback(
    (segment: DeckBoardSegment, point: PlanePoint | null) => {
      if (!point || !segment.differentBoardKey) {
        return;
      }

      const nextPosition = getDifferentBoardPositionForPoint(segment, point);

      if (!nextPosition || nextPosition === segment.differentBoardPosition) {
        return;
      }

      onDifferentBoardPositionChange?.(segment.differentBoardKey, nextPosition);
    },
    [onDifferentBoardPositionChange],
  );

  const startDifferentBoardDrag = useCallback<DifferentBoardDragHandler>(
    (event, segment, toPlanePointFromClient) => {
      if (event.button !== 0 || !segment.differentBoardKey) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      differentBoardDragRef.current = {
        key: segment.differentBoardKey,
        pointerId: event.pointerId,
      };
      moveDifferentBoardToPoint(segment, toPlanePointFromClient(event.clientX, event.clientY));
    },
    [moveDifferentBoardToPoint],
  );

  const dragDifferentBoard = useCallback<DifferentBoardDragHandler>(
    (event, segment, toPlanePointFromClient) => {
      event.preventDefault();
      event.stopPropagation();

      const dragState = differentBoardDragRef.current;

      if (!dragState || dragState.pointerId !== event.pointerId || dragState.key !== segment.differentBoardKey) {
        return;
      }

      moveDifferentBoardToPoint(segment, toPlanePointFromClient(event.clientX, event.clientY));
    },
    [moveDifferentBoardToPoint],
  );

  const finishDifferentBoardDrag = useCallback<DifferentBoardDragHandler>(
    (event, segment, toPlanePointFromClient) => {
      event.preventDefault();
      event.stopPropagation();

      moveDifferentBoardToPoint(segment, toPlanePointFromClient(event.clientX, event.clientY));

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      differentBoardDragRef.current = null;
    },
    [moveDifferentBoardToPoint],
  );

  // Memoized: CartesianPlane's export-mode fit effect keys on this reference, so a new object on every
  // render made the always-mounted capture copy re-measure and re-render on each form change.
  const fitBounds = useMemo<PlaneBounds | null>(() => getCoordinateBounds(coordinates), [coordinates]);

  const tools = (
    <>
      <div className={styles.pointTools}>
        <div className={styles.pointCount}>{`${coordinates.length}/${pointLimit}`}</div>
        <button
          type="button"
          className={styles.iconButton}
          onClick={clearCoordinates}
          title={commonTranslation.clearPoints}
          disabled={readOnly || coordinates.length === 0}
        >
          <Icon glyph={DeleteIcon} className={styles.icon} />
        </button>
        {onShowHint && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={onShowHint}
            title={calculationTranslation.showPlaneInteractionHint}
          >
            <Icon glyph={InfoIcon} className={styles.icon} />
          </button>
        )}
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </>
  );

  return (
    <div className={styles.wrapper}>
      <CartesianPlane
        contentRef={planeContentRef}
        exportMode={exportMode}
        fitBounds={exportMode ? fitBounds : null}
        onPointClick={readOnly ? undefined : addCoordinate}
        pointStore={pointStore}
        toolbarContent={tools}
      >
        {({ isPanning, toPlanePointFromClient, toScreenPoint }) => (
          <DeckDrawingLayers
            boardSegments={boardSegments}
            boardWidth={boardWidth}
            coordinates={coordinates}
            draftCoordinates={draftCoordinates}
            draggedSideIndex={draggedSideIndex}
            exportMode={exportMode}
            finishCoordinates={finishCoordinates}
            hasHorizontalBoards={hasHorizontalBoards}
            isPanning={isPanning}
            lineClassName={shapeConfig.lineClassName}
            onDifferentBoardDrag={dragDifferentBoard}
            onDifferentBoardDragEnd={finishDifferentBoardDrag}
            onDifferentBoardDragStart={startDifferentBoardDrag}
            onDimensionChange={onDimensionChange}
            onSideDrag={dragSide}
            onSideDragEnd={finishSideDrag}
            onSideDragStart={startSideDrag}
            pointLimit={pointLimit}
            pointStore={pointStore}
            readOnly={readOnly}
            shapeClassName={shapeConfig.shapeClassName}
            slopeArrow={slopeArrow}
            toPlanePointFromClient={toPlanePointFromClient}
            toScreenPoint={toScreenPoint}
            typedDistance={typedDistance}
          />
        )}
      </CartesianPlane>
    </div>
  );
};

export default DeckDrawingPlane;
